import { useParams, useNavigate } from 'react-router-dom'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  SearchInput,
  TabsContainer,
  LoadingPage,
  UserStatusBadge,
  UserRoleBadge,
  ButtonVariant,
  UserIcon,
  UserCard,
} from '@valiant/ui-shared'
import { useGetUserQuery, UserRole } from '../generated/graphql'
import { useEffect, useMemo, useState } from 'react'
import { CompaniesTab } from '../components/tabs/CompaniesTab'
import { LogsTab } from '../components/tabs/LogsTab'
import { SwapsTab } from '../components/tabs/SwapsTab'
import { useQueryParams } from '@valiant/ui-shared'
import { MainLayout } from '../components/layout/MainLayout'
import { Settings } from 'lucide-react'

export function User() {
  const { userId } = useParams<{ userId: string }>()

  const navigate = useNavigate()
  const queryParams = useQueryParams('companies')
  const [searchTerm, setSearchTerm] = useState('')

  const [{ data: userData, fetching: userLoading, error: userError }] = useGetUserQuery({
    variables: { userId: userId! },
    pause: !userId,
  })

  const user = userData?.getUser

  useEffect(() => {
    setSearchTerm('')
  }, [queryParams.tab])

  useEffect(() => {
    if (user && user.role !== UserRole.Trader) {
      navigate('/not-found')
    }
  }, [user, navigate])

  const badges = useMemo(() => {
    if (!user) return []
    return [
      {
        node: <UserRoleBadge role={user.role} />,
        direction: 'LEFT',
      },
      {
        node: (
          <UserStatusBadge
            isWhitelisted={user.isWhitelisted}
            isTrader={user.role === UserRole.Trader}
            isActive={user.isActive}
          />
        ),
        direction: 'LEFT',
      },
    ]
  }, [user])

  const buttons = useMemo(() => {
    if (!user) return []
    return [
      {
        label: 'Manage',
        variant: ButtonVariant.MAIN_ACTION,
        onClick: () => navigate(`/users/${user.id}/settings`),
        icon: <Settings className="h-3 w-3 md:h-4 md:w-4" />,
      },
    ]
  }, [user, navigate])

  if (userLoading) {
    return <LoadingPage />
  }

  if (userError || !user) {
    return (
      <MainLayout>
        <div>User not found</div>
      </MainLayout>
    )
  }

  return (
    <MainLayout
      breadcrumbs={[
        {
          label: user.username,
          icon: <UserIcon />,
          hideImage: true,
        },
      ]}
      title={user.username}
      badges={badges}
      buttons={buttons}
    >
      {/* Stats Cards */}
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
        <UserCard email={user.email} role={user.role} isWhitelisted={user.isWhitelisted} />
      </div>

      {/* Tabs Section */}
      <Tabs
        value={queryParams.tab || 'companies'}
        onValueChange={(value) => {
          // Clear transactions filter when switching to other tabs
          if (value !== 'transactions') {
            queryParams.updateMultiple({
              tab: value,
              status: undefined,
              type: undefined,
              isFlagged: undefined,
              hasFunds: value === 'companies' ? queryParams.hasFunds : undefined,
            })
          } else {
            queryParams.updateTab(value)
          }
        }}
        className="space-y-4"
      >
        <TabsContainer>
          <TabsList>
            <TabsTrigger value="companies">Companies</TabsTrigger>
            <TabsTrigger value="swaps">Swaps</TabsTrigger>
            <TabsTrigger value="logs">Logs</TabsTrigger>
          </TabsList>

          <SearchInput
            placeholder="Search"
            className="w-[200px]"
            value={searchTerm}
            onChange={setSearchTerm}
          />
        </TabsContainer>

        <TabsContent value="companies">
          <CompaniesTab userId={user.id} searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="swaps">
          <SwapsTab userId={user.id} searchTerm={searchTerm} />
        </TabsContent>

        <TabsContent value="logs">
          <LogsTab userId={user.id} searchTerm={searchTerm} />
        </TabsContent>
      </Tabs>
    </MainLayout>
  )
}
